import { uiSurface } from "shared/ui/uiSurface"
import {
  Block,
  CheckCircle,
  CheckCircleOutline,
  DeleteOutline,
  DirectionsCar,
} from "@mui/icons-material"
import { Avatar, Chip, IconButton, Stack, Tooltip, Typography } from "@mui/material"
import {
  DataGrid,
  GridColDef,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid"
import dayjs from "dayjs"
import { FC, useCallback, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { CourierLinkItem } from "../../apis/appApi"
import { getAvatarColor } from "../customers/utils"

type Props = {
  rows: CourierLinkItem[]
  loading: boolean
  rowCount: number
  paginationModel: GridPaginationModel
  onPaginationModelChange: (model: GridPaginationModel) => void
  sortModel: GridSortModel
  onSortModelChange: (model: GridSortModel) => void
  onRowClick: (id: number) => void
  onAccept: (link: CourierLinkItem) => void
  onDelete: (link: CourierLinkItem) => void
}

function getStatusChipProps(state: string, t: (key: string) => string) {
  switch (state) {
    case "ESTABLISHED":
      return { label: t("courierLinks.status.established"), sx: { bgcolor: "#e8f5e9", color: "#2e7d32" } }
    case "INITIATED_WAITING_HAULER":
      return { label: t("courierLinks.status.waitingHauler"), sx: { bgcolor: "#fff3e0", color: "#e65100" } }
    case "INITIATED_WAITING_PROVIDER":
      return { label: t("courierLinks.status.waitingProvider"), sx: { bgcolor: "#fff3e0", color: "#e65100" } }
    case "REFUSED":
      return { label: t("courierLinks.status.refused"), sx: { bgcolor: "#ffebee", color: "#c62828" } }
    default:
      return { label: state, sx: {} }
  }
}

function VisibilityIcons({ config }: { config: CourierLinkItem["config"] }) {
  if (!config) {
    return (
      <Typography variant="body2" color="text.disabled">
        {"\u2014"}
      </Typography>
    )
  }

  const posIcon =
    config.positionVisibility === "ALWAYS" ? (
      <Tooltip title="Position: Always">
        <CheckCircleOutline sx={{ fontSize: 18, color: "#2e7d32" }} />
      </Tooltip>
    ) : config.positionVisibility === "ONLY_WHEN_CARRYING" ? (
      <Tooltip title="Position: When carrying">
        <DirectionsCar sx={{ fontSize: 18, color: "#f57c00" }} />
      </Tooltip>
    ) : (
      <Tooltip title="Position: Never">
        <Block sx={{ fontSize: 18, color: "#c62828" }} />
      </Tooltip>
    )

  const etaIcon =
    config.etaVisibility === "ALWAYS" ? (
      <Tooltip title="ETA: Always">
        <CheckCircleOutline sx={{ fontSize: 18, color: "#2e7d32" }} />
      </Tooltip>
    ) : (
      <Tooltip title="ETA: Never">
        <Block sx={{ fontSize: 18, color: "#c62828" }} />
      </Tooltip>
    )

  const orderIcon =
    config.numberOrderInCar === "ALL" ? (
      <Tooltip title="Order count: All">
        <CheckCircleOutline sx={{ fontSize: 18, color: "#2e7d32" }} />
      </Tooltip>
    ) : (
      <Tooltip title="Order count: None">
        <Block sx={{ fontSize: 18, color: "#c62828" }} />
      </Tooltip>
    )

  return (
    <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
      {posIcon}
      {etaIcon}
      {orderIcon}
    </Stack>
  )
}

export const CourierLinksGrid: FC<Props> = ({
  rows,
  loading,
  rowCount,
  paginationModel,
  onPaginationModelChange,
  sortModel,
  onSortModelChange,
  onRowClick,
  onAccept,
  onDelete,
}) => {
  const { t } = useTranslation()

  const columns: GridColDef[] = useMemo(
    () => [
      {
        field: "hauler",
        headerName: t("courierLinks.col.hauler"),
        flex: 1.5,
        minWidth: 200,
        valueGetter: (_value, row) => row.hauler?.name ?? "",
        renderCell: (params) => {
          const hauler = params.row.hauler
          if (!hauler) return null
          const initial = hauler.name?.charAt(0)?.toUpperCase() || "?"
          const color = getAvatarColor(hauler.name)
          return (
            <Stack direction="row" spacing={1.5} alignItems="center" sx={{ height: "100%" }}>
              <Avatar
                sx={{
                  width: 32,
                  height: 32,
                  fontSize: 13,
                  fontWeight: 600,
                  bgcolor: color,
                }}
              >
                {initial}
              </Avatar>
              <Stack sx={{ minWidth: 0 }}>
                <Typography variant="body2" fontWeight={600} noWrap>
                  {hauler.name}
                </Typography>
                <Typography variant="caption" color="text.secondary" noWrap>
                  {hauler.code}
                </Typography>
              </Stack>
            </Stack>
          )
        },
      },
      {
        field: "address",
        headerName: t("courierLinks.col.address"),
        flex: 1,
        minWidth: 160,
        valueGetter: (_value, row) => row.hauler?.address ?? "",
        renderCell: (params) => {
          const addr = params.row.hauler?.address
          return (
            <Typography variant="body2" color={addr ? undefined : "text.disabled"} noWrap>
              {addr || "\u2014"}
            </Typography>
          )
        },
      },
      {
        field: "contact",
        headerName: t("courierLinks.col.contact"),
        flex: 0.8,
        minWidth: 120,
        valueGetter: (_value, row) =>
          row.acceptingUser
            ? `${row.acceptingUser.firstName} ${row.acceptingUser.lastName}`
            : "",
        renderCell: (params) => {
          const user = params.row.acceptingUser
          return (
            <Typography variant="body2" color={user ? undefined : "text.disabled"} noWrap>
              {user ? `${user.firstName} ${user.lastName}` : "\u2014"}
            </Typography>
          )
        },
      },
      {
        field: "state",
        headerName: t("courierLinks.col.status"),
        flex: 0.8,
        minWidth: 140,
        renderCell: (params) => {
          const chipProps = getStatusChipProps(params.value, t)
          return (
            <Stack justifyContent="center" sx={{ height: "100%" }}>
              <Chip label={chipProps.label} size="small" sx={chipProps.sx} />
              {params.value === "ESTABLISHED" && params.row.acceptedDate && (
                <Typography variant="caption" color="text.secondary">
                  {dayjs(params.row.acceptedDate).format("MMM D, YYYY")}
                </Typography>
              )}
            </Stack>
          )
        },
      },
      {
        field: "visibility",
        headerName: t("courierLinks.col.visibility"),
        flex: 0.6,
        minWidth: 100,
        sortable: false,
        renderCell: (params) => <VisibilityIcons config={params.row.config} />,
      },
      {
        field: "actions",
        headerName: t("courierLinks.col.actions"),
        flex: 0.4,
        minWidth: 80,
        sortable: false,
        renderCell: (params) => {
          const row = params.row as CourierLinkItem
          return (
            <Stack direction="row" spacing={0.5} alignItems="center" sx={{ height: "100%" }}>
              {row.state === "INITIATED_WAITING_PROVIDER" && (
                <IconButton
                  size="small"
                  data-testid={`links-accept-btn-${row.id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onAccept(row)
                  }}
                  sx={{ color: "#2e7d32" }}
                >
                  <CheckCircle fontSize="small" />
                </IconButton>
              )}
              {row.state !== "REFUSED" && (
                <IconButton
                  size="small"
                  data-testid={`links-delete-btn-${row.id}`}
                  onClick={(e) => {
                    e.stopPropagation()
                    onDelete(row)
                  }}
                  sx={{ color: "#c62828" }}
                >
                  <DeleteOutline fontSize="small" />
                </IconButton>
              )}
            </Stack>
          )
        },
      },
    ],
    [t, onAccept, onDelete],
  )

  const handleRowClick = useCallback(
    (params: any) => {
      onRowClick(params.id as number)
    },
    [onRowClick],
  )

  return (
    <DataGrid
      {...uiSurface("UI-DLV2-131")}
      data-testid="courier-links-grid"
      rows={rows}
      columns={columns}
      loading={loading}
      rowCount={rowCount}
      paginationMode="server"
      paginationModel={paginationModel}
      onPaginationModelChange={onPaginationModelChange}
      sortingMode="server"
      sortModel={sortModel}
      onSortModelChange={onSortModelChange}
      pageSizeOptions={[10, 25, 50]}
      onRowClick={handleRowClick}
      disableRowSelectionOnClick
      sx={{
        border: 0,
        height: "100%",
        "& .MuiDataGrid-row": { cursor: "pointer" },
      }}
    />
  )
}
